import { DownloadOutlined, InboxOutlined, PrinterOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, App, Button, Card, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, Upload } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useRef, useState } from "react";

import { formatTime } from "../../api/format";
import { useDeletePaper, useDownloadPaperSource, usePapers, usePreviewPaper, usePublishPaper, useSetCurrentPaper, useSetPaperOpensAt } from "../../api/papers";
import { EXAM_GROUP_NAMES, GROUP_NAMES, type ExamGroupId, type Group, type PaperBuildSummary, type PaperItem } from "../../api/types";
import { useAuthenticated } from "../../auth/SessionProvider";

const NO_GROUP = "__none__";
const BEIJING_OFFSET_MINUTES = 8 * 60;

/** A picker value whose wall-clock time is Beijing time, regardless of the browser's zone. */
function toBeijing(iso: string): Dayjs {
  const shift = (BEIJING_OFFSET_MINUTES + new Date().getTimezoneOffset()) * 60_000;
  return dayjs(new Date(new Date(iso).getTime() + shift));
}

function fromBeijing(value: Dayjs): string {
  return new Date(`${value.format("YYYY-MM-DDTHH:mm:00")}+08:00`).toISOString();
}

interface UploadValues {
  id: string;
  group: string;
  opensAt: Dayjs;
  defaultScore: number;
}

export function PapersPage() {
  const papers = usePapers();
  const publish = usePublishPaper();
  const preview = usePreviewPaper();
  const setOpensAt = useSetPaperOpensAt();
  const setCurrent = useSetCurrentPaper();
  const remove = useDeletePaper();
  const download = useDownloadPaperSource();
  const { principal } = useAuthenticated();
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState<{ name: string; text: string }>();
  const [checked, setChecked] = useState<{ summary?: PaperBuildSummary; error?: string; forText?: string }>();
  const [editingOpensAt, setEditingOpensAt] = useState<PaperItem>();
  const [switching, setSwitching] = useState<{ item: PaperItem; groupId: ExamGroupId }>();
  const [typed, setTyped] = useState("");
  const [withAnswers, setWithAnswers] = useState(false);
  const [form] = Form.useForm<UploadValues>();
  const [opensAtForm] = Form.useForm<{ opensAt: Dayjs }>();
  const frame = useRef<HTMLIFrameElement>(null);

  const scopedGroups = (Object.keys(GROUP_NAMES) as Group[]).filter((group) => principal.scope.groups === "all" || principal.scope.groups.includes(group));
  const items = papers.data?.items ?? [];
  const bindings = papers.data?.bindings ?? {};
  const currentOf = (groupId: ExamGroupId) => items.find((item) => item.examKey === bindings[groupId]?.examKey);

  const runPublish = async (dryRun: boolean) => {
    let values: UploadValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!source) return;
    try {
      const result = await publish.mutateAsync({
        id: values.id.trim(),
        ...(values.group === NO_GROUP ? {} : { group: values.group as Group }),
        opensAt: fromBeijing(values.opensAt),
        defaultScore: values.defaultScore,
        source: source.text,
        dryRun
      });
      if (!result.ok) {
        setChecked({ error: result.message, forText: source.text });
        return;
      }
      if (result.dryRun) {
        setChecked({ summary: result, forText: source.text });
        return;
      }
      message.success(result.created ? `已发布版本 ${result.contentHash.slice(0, 16)}` : "该版本此前已发布，未做改动");
      setUploading(false);
      setChecked(undefined);
      setSource(undefined);
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "请求失败");
    }
  };

  const validated = checked?.summary !== undefined && checked.forText === source?.text;

  const showPreview = async (item: PaperItem, answers: boolean) => {
    setWithAnswers(answers);
    try {
      await preview.mutateAsync({ examKey: item.examKey, withAnswers: answers });
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "预览失败");
    }
  };

  const saveOpensAt = async ({ opensAt }: { opensAt: Dayjs }) => {
    if (!editingOpensAt) return;
    try {
      await setOpensAt.mutateAsync({ examKey: editingOpensAt.examKey, opensAt: fromBeijing(opensAt) });
      message.success("已更新开考时间，考试端最多 30 秒后生效");
      setEditingOpensAt(undefined);
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "保存失败");
    }
  };

  const currentOpened = switching && switching.groupId !== "simulation" ? (() => {
    const current = currentOf(switching.groupId);
    return current !== undefined && new Date(current.opensAt).getTime() <= Date.now();
  })() : false;

  const confirmSwitch = async () => {
    if (!switching) return;
    try {
      const result = await setCurrent.mutateAsync({ groupId: switching.groupId, examKey: switching.item.examKey, confirm: currentOpened });
      message.success(switching.groupId === "simulation"
        ? "已更新模拟练习卷"
        : `已将 ${result.rebound} 名考生改绑到新版本${result.failures.length ? `，${result.failures.length} 人失败` : ""}`);
      setSwitching(undefined);
      setTyped("");
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "切换失败");
    }
  };

  const act = async (run: () => Promise<unknown>, success: string) => {
    try {
      await run();
      message.success(success);
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "操作失败");
    }
  };

  const groupTag = (group: Group | null) => group ? <Tag color={group === "youth" ? "blue" : "purple"}>{GROUP_NAMES[group]}</Tag> : <Tag>练习卷</Tag>;
  const targets = (item: PaperItem): ExamGroupId[] => [...(item.group ? [item.group] : []), "simulation"];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => { form.resetFields(); setSource(undefined); setChecked(undefined); setUploading(true); }}>上传新试卷</Button>
        <Button icon={<ReloadOutlined />} loading={papers.isFetching} onClick={() => void papers.refetch()}>刷新</Button>
        <Typography.Text type="secondary">上传只产生一个新版本；点“设为当前试卷”才会改绑该组全部考生。开考时间可随时修改。</Typography.Text>
      </Space>
      {papers.error && <Alert type="error" showIcon message={papers.error.message} />}
      <Space wrap align="start">
        {([...scopedGroups, "simulation"] as ExamGroupId[]).map((groupId) => {
          const current = currentOf(groupId);
          return (
            <Card key={groupId} size="small" title={`${EXAM_GROUP_NAMES[groupId]}当前试卷`} style={{ minWidth: 300 }}>
              {current ? (
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="标题">{current.title}</Descriptions.Item>
                  <Descriptions.Item label="版本">{current.id} · {current.contentHash.slice(0, 16)}</Descriptions.Item>
                  <Descriptions.Item label="开考时间">{formatTime(current.opensAt)}</Descriptions.Item>
                  {groupId !== "simulation" && <Descriptions.Item label="考生">{papers.data?.candidates[groupId] ?? 0} 人</Descriptions.Item>}
                </Descriptions>
              ) : <Typography.Text type="secondary">尚未设置</Typography.Text>}
            </Card>
          );
        })}
      </Space>
      <Table<PaperItem>
        rowKey="examKey"
        size="middle"
        loading={papers.isLoading}
        dataSource={items}
        pagination={false}
        columns={[
          { title: "试卷", render: (_, item) => <><div>{item.id}</div><Typography.Text type="secondary" code>{item.contentHash.slice(0, 16)}</Typography.Text></> },
          { title: "标题", dataIndex: "title" },
          { title: "组别", dataIndex: "group", render: groupTag },
          { title: "题数 / 满分", render: (_, item) => `${item.questionCount} / ${item.totalScore}` },
          { title: "开考时间", dataIndex: "opensAt", render: (value: string, item) => <a onClick={() => { opensAtForm.setFieldsValue({ opensAt: toBeijing(value) }); setEditingOpensAt(item); }}>{formatTime(value)}</a> },
          { title: "当前使用", render: (_, item) => item.currentFor.length ? item.currentFor.map((groupId) => <Tag key={groupId} color="green">{EXAM_GROUP_NAMES[groupId]}</Tag>) : <Typography.Text type="secondary">—</Typography.Text> },
          { title: "绑定考生", dataIndex: "boundCandidates" },
          { title: "上传", render: (_, item) => <><div>{formatTime(item.createdAt)}</div><Typography.Text type="secondary">{item.createdBy}</Typography.Text></> },
          { title: "操作", render: (_, item) => (
            <Space size={4} wrap>
              <a onClick={() => void showPreview(item, false)}>预览</a>
              {targets(item).filter((groupId) => !item.currentFor.includes(groupId) && (groupId === "simulation" || scopedGroups.includes(groupId))).map((groupId) => (
                <a key={groupId} onClick={() => { setTyped(""); setSwitching({ item, groupId }); }}>设为{EXAM_GROUP_NAMES[groupId]}{groupId === "simulation" ? "卷" : "当前试卷"}</a>
              ))}
              <a onClick={() => void act(() => download.mutateAsync({ examKey: item.examKey, fallbackName: `${item.id}.md` }), "已下载源文件")}>源文件</a>
              {item.currentFor.length === 0 && item.boundCandidates === 0 && (
                <Popconfirm title="删除该版本？" description="只删除元数据和答案，CDN 上的题目文件保留。" onConfirm={() => void act(() => remove.mutateAsync(item.examKey), "已删除")}>
                  <a style={{ color: "#cf1322" }}>删除</a>
                </Popconfirm>
              )}
            </Space>
          ) }
        ]}
      />
      {preview.error && <Alert type="error" showIcon message={preview.error.message} />}
      {preview.data && (
        <>
          <Space>
            <Typography.Text strong>{preview.data.title}（{preview.data.examKey}）</Typography.Text>
            <Switch checked={withAnswers} checkedChildren="显示答案" unCheckedChildren="不显示答案" loading={preview.isPending}
              onChange={(value) => { const item = items.find((entry) => entry.examKey === preview.data?.examKey); if (item) void showPreview(item, value); }} />
            <Button icon={<PrinterOutlined />} onClick={() => frame.current?.contentWindow?.print()}>打印</Button>
            <Button icon={<DownloadOutlined />} onClick={() => {
              const blob = new Blob([preview.data!.html], { type: "text/html;charset=utf-8" });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `${preview.data!.examKey.replace(":", "-")}${withAnswers ? "-with-answers" : ""}.html`;
              link.click();
              URL.revokeObjectURL(link.href);
            }}>下载 HTML</Button>
          </Space>
          <iframe ref={frame} title="试卷预览" srcDoc={preview.data.html} className="sheet-frame" />
        </>
      )}

      <Modal
        open={uploading}
        title="上传新试卷"
        onCancel={() => setUploading(false)}
        footer={[
          <Button key="cancel" onClick={() => setUploading(false)}>取消</Button>,
          <Button key="check" onClick={() => void runPublish(true)} disabled={!source} loading={publish.isPending}>校验</Button>,
          <Button key="publish" type="primary" onClick={() => void runPublish(false)} disabled={!validated} loading={publish.isPending}>发布</Button>
        ]}
        destroyOnHidden
        width={640}
      >
        <Form<UploadValues> form={form} layout="vertical" requiredMark={false} initialValues={{ group: scopedGroups[0] ?? NO_GROUP, defaultScore: 3 }}>
          <Form.Item name="id" label="试卷 ID" rules={[{ required: true, message: "请输入试卷 ID" }, { pattern: /^[a-z0-9][a-z0-9-]{1,63}$/, message: "小写字母、数字和连字符，2 到 64 位" }]} extra="同一考试再次上传请沿用同一 ID；内容不同即成为新版本。">
            <Input placeholder="例如 2026-lmcc-youth" maxLength={64} />
          </Form.Item>
          <Form.Item name="group" label="组别" rules={[{ required: true }]}>
            <Select options={[...scopedGroups.map((group) => ({ value: group, label: GROUP_NAMES[group] })), { value: NO_GROUP, label: "无（练习卷）" }]} />
          </Form.Item>
          <Form.Item name="opensAt" label="开考时间（北京时间）" rules={[{ required: true, message: "请选择开考时间" }]}>
            <DatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="defaultScore" label="默认每题分值" rules={[{ required: true }]} extra="章节标题里的 [score=N] 会覆盖其下题目的分值。">
            <InputNumber min={1} max={1000} style={{ width: 160 }} />
          </Form.Item>
          <Upload.Dragger
            accept=".md,text/markdown,text/plain"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              const reader = new FileReader();
              reader.onload = () => { setSource({ name: file.name, text: String(reader.result ?? "") }); setChecked(undefined); };
              reader.readAsText(file, "utf-8");
              return false;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖入 exam.md</p>
            <p className="ant-upload-hint">{source ? `已选择 ${source.name}（${Math.round(source.text.length / 1024)} KB）` : "标题取一级标题；题目、选项和 [Answer] 标记按试卷格式书写"}</p>
          </Upload.Dragger>
          {checked?.error && <Alert style={{ marginTop: 16 }} type="error" showIcon message="校验未通过" description={checked.error} />}
          {validated && checked?.summary && (
            <Descriptions style={{ marginTop: 16 }} size="small" bordered column={2}>
              <Descriptions.Item label="标题" span={2}>{checked.summary.title}</Descriptions.Item>
              <Descriptions.Item label="题数">{checked.summary.questionCount}</Descriptions.Item>
              <Descriptions.Item label="满分">{checked.summary.totalScore}</Descriptions.Item>
              <Descriptions.Item label="版本" span={2}>{checked.summary.contentHash.slice(0, 16)}{checked.summary.exists ? "（已存在，发布不会改动）" : ""}</Descriptions.Item>
            </Descriptions>
          )}
        </Form>
      </Modal>

      <Modal open={editingOpensAt !== undefined} title={`修改开考时间：${editingOpensAt?.id ?? ""}`} onCancel={() => setEditingOpensAt(undefined)} onOk={() => opensAtForm.submit()} confirmLoading={setOpensAt.isPending} okText="保存" destroyOnHidden>
        <Form form={opensAtForm} layout="vertical" onFinish={(values) => void saveOpensAt(values)} requiredMark={false}>
          <Form.Item name="opensAt" label="开考时间（北京时间）" rules={[{ required: true, message: "请选择开考时间" }]}>
            <DatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={switching !== undefined}
        title={switching ? `设为${EXAM_GROUP_NAMES[switching.groupId]}${switching.groupId === "simulation" ? "卷" : "当前试卷"}` : ""}
        onCancel={() => setSwitching(undefined)}
        onOk={() => void confirmSwitch()}
        okButtonProps={{ disabled: currentOpened && typed !== switching?.groupId, danger: currentOpened }}
        confirmLoading={setCurrent.isPending}
        okText="确认切换"
        destroyOnHidden
      >
        {switching && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>{switching.item.title}（{switching.item.id} · {switching.item.contentHash.slice(0, 16)}）</div>
            {switching.groupId === "simulation"
              ? <div>test/test 模拟账号将改用这份试卷。</div>
              : <div>{EXAM_GROUP_NAMES[switching.groupId]}全部 {papers.data?.candidates[switching.groupId] ?? 0} 名考生将改绑到该版本；旧版本上的答案保留在旧版本，不会带入新版本。</div>}
            {currentOpened && (
              <>
                <Alert type="error" showIcon message="该组别的考试已经开始，切换试卷会影响正在答题的考生。" />
                <Input placeholder={`输入 ${switching.groupId} 以确认`} value={typed} onChange={(event) => setTyped(event.target.value.trim())} />
              </>
            )}
          </Space>
        )}
      </Modal>
    </Space>
  );
}
